import Service from '@/services/Service';

import {
  ServerProductListService,
  type GridItem
} from '@/services/serverless/ServerProductListService';

import type { IGetProductListBySearchTermParams } from '@/services/serverless/ServerProductListService/IGetProductListBySearchTermParams';
import type { ICategoryProductListParams } from '@/services/models/Category';
import type {
  IProductList,
  SearchProductListMetadata
} from '@/services/serverless/ServerProductListService/IProductList';
import { assertRSCSerializableParams } from '@/services/utils/react-utils/rsc-utils/server-function-utils';
import ProductListServiceMock from './ProductListServiceMock';
import { streamProductsFromSkus } from './server-functions/streamProductsFromSkus';
import { streamCategoryProducts } from './server-functions/streamCategoryProducts';
import { streamProductsFromSearch } from './server-functions/streamProductsFromSearch';
import I18NService from '../I18NService';
import { EnvironmentService } from '../EnvironmentService';

/**
 * Abstracts the generation and manipulation of the product list.
 */
export class ProductListService extends Service {
  /**
   * Fetches products from the server in the form of a streamable promise based
   * on a search term.
   *
   * If on the server it returns this directly, if not it calls the server
   * function which calls this method.
   *
   * @param term - The search term to search for.
   * @param params - The parameters for the product search request.
   * @returns The Paginated product list with the products that match the search
   * term and criteria and additional metadata.
   */
  public async streamProductsFromSearch(
    term: string,
    params: Omit<IGetProductListBySearchTermParams, 'locale'>
  ): Promise<IProductList<SearchProductListMetadata>> {
    const locale = I18NService.currentLocale.toString();

    if ((typeof window === "undefined")) {
      return ServerProductListService.streamProductsFromSearch(term, {
        locale,
        ...params
      });
    }

    // Assert parameters are RSC-serializable
    const actualParams = { term, locale, ...params };

    assertRSCSerializableParams(actualParams);
    return streamProductsFromSearch(actualParams);
  }

  /**
   * List the products that belong to a specified category.
   *
   * If on the server, it calls the {@link ServerProductListService} directly.
   * Otherwise, it calls a server action that then invokes the server-side
   * service.
   *
   * @param listParams - The parameters to use for this list operation.
   * @returns A paginated list response, as an {@link IProductList} object.
   */
  public async streamCategoryProducts(
    listParams: ICategoryProductListParams
  ): Promise<IProductList> {
    if ((typeof window === "undefined")) {
      return ServerProductListService.streamCategoryProducts(listParams);
    }

    // Assert parameters are RSC-serializable
    assertRSCSerializableParams(listParams);
    return streamCategoryProducts(listParams);
  }

  /**
   * Fetches product data from the server in the form of a streamable promise if
   * on the server it returns this directly, if not it calls the server function
   * which calls this method.
   * @param skus - An array of skus.
   * @returns An array of product DTOs.
   */
  public async streamProductsFromSkus(
    skus: Array<string>
  ): Promise<Array<GridItem>> {
    if ((typeof window === "undefined")) {
      return ServerProductListService.streamProductsFromSkus(skus);
    }

    // Assert parameters are RSC-serializable
    const params = {
      products: skus,
      locale: I18NService.currentLocale.toString()
    };

    assertRSCSerializableParams(params);
    return streamProductsFromSkus(params);
  }
}

const _mock = new ProductListServiceMock(ProductListService);
export default ProductListService.withMock(_mock);
